import { useState, useEffect, useRef, useCallback } from "react";
import { CACHE_DURATION, AIRPORTS, REGIONAL_AIRSPACE } from "./constants";
import { fetchWeatherFromAPI } from "./utils";
import { saveToCache, loadFromCache } from "./cacheUtils";

/**
 * Weather hook — fetches current weather for hub airports only.
 * Used by the Header component for temperature and day/night display.
 */
function useWeather(selectedHub) {
  const [weatherData, setWeatherData] = useState({});
  const [weatherLoading, setWeatherLoading] = useState(true);
  const cacheRef = useRef({});

  const getHubCodes = useCallback(() => {
    const codes = new Set();
    if (selectedHub === REGIONAL_AIRSPACE.hubCode) {
      AIRPORTS.forEach((a) => codes.add(a.iataCode));
    } else {
      const airport = AIRPORTS.find((a) => a.hubCode === selectedHub);
      if (airport) codes.add(airport.iataCode);
    }
    return codes;
  }, [selectedHub]);

  const fetchAllWeather = useCallback(async () => {
    const codes = getHubCodes();
    if (codes.size === 0) {
      setWeatherLoading(false);
      return;
    }

    const now = Date.now();

    try {
      setWeatherLoading(true);
      const results = {};

      // Fetch weather sequentially to avoid Lambda cold start 500s
      const fetched = [];
      for (const code of Array.from(codes)) {
        const cached = cacheRef.current[code];
        if (cached && now - cached.timestamp < CACHE_DURATION.WEATHER) {
          fetched.push({ code, data: cached.data, cached: true });
          continue;
        }
        try {
          const data = await fetchWeatherFromAPI(code);
          fetched.push({ code, data, cached: false });
        } catch (error) {
          console.error(`Failed to fetch weather for ${code}:`, error);
          const stored = loadFromCache(
            `weather_${code}`,
            CACHE_DURATION.WEATHER,
          );
          if (stored) {
            cacheRef.current[code] = {
              data: stored.data,
              timestamp: stored.timestamp,
            };
            fetched.push({ code, data: stored.data, cached: true });
          } else {
            fetched.push({
              code,
              data: {
                current_weather: {
                  condition: "Sunny",
                  temperature: 80,
                  is_day: 1,
                },
              },
              error: true,
            });
          }
        }
      }

      fetched.forEach(({ code, data, cached }) => {
        results[code] = data;
        if (!cached) {
          cacheRef.current[code] = { data, timestamp: now };
          saveToCache(`weather_${code}`, data);
        }
      });

      setWeatherData((prev) => ({ ...prev, ...results }));
    } catch (error) {
      console.error("Error fetching weather data:", error);
    } finally {
      setWeatherLoading(false);
    }
  }, [getHubCodes]);

  useEffect(() => {
    const lastFetch = { ts: 0 };
    const trackedFetch = () =>
      fetchAllWeather().then(() => {
        lastFetch.ts = Date.now();
      });

    trackedFetch();
    const mainInterval = setInterval(trackedFetch, CACHE_DURATION.WEATHER);

    const heartbeat = setInterval(() => {
      if (Date.now() - lastFetch.ts >= CACHE_DURATION.WEATHER) {
        trackedFetch();
      }
    }, 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (Date.now() - lastFetch.ts >= CACHE_DURATION.WEATHER) {
          trackedFetch();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(mainInterval);
      clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchAllWeather]);

  return { weatherData, weatherLoading };
}

export default useWeather;
